import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createFinanceAccount,
  fetchBankAccounts,
  fetchBankSyncStatus,
  getApiErrorMessage,
  patchFinanceAccount,
  putFinanceAccountLink,
  startBankSync,
} from "../../api";
import ErrorRetry from "../../components/ErrorRetry";
import Loading from "../../components/Loading";
import FinanceTabs from "./FinanceTabs";
import {
  OWNERS,
  ROLES,
  bankClosedLabel,
  expectsBankSlots,
  formatMoney,
  formatPct,
  ownerLabel,
  parseAmount,
  roleLabel,
} from "./financeUtils";
import useFinanceOverview from "./useFinanceOverview";

const EMPTY_FORM = {
  kind: "account",
  title: "",
  owner: "firm",
  pct: "",
  role: "",
};

function parseBankDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function formatBankClock(value) {
  const date = parseBankDate(value);
  if (!date) return "—";
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

function formatBankStamp(value) {
  const date = parseBankDate(value);
  if (!date) return "—";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${formatBankClock(value)} ${day}.${month}`;
}

function formatBankBalance(value) {
  return value == null ? "—" : formatMoney(value);
}

function bankOptionLabel(item) {
  const rest = `${item.owner_label} · …${item.tail} · ${formatBankBalance(item.balance)}`;
  return item.name ? `${item.name} · ${rest}` : rest;
}

const TOCHKA_OWNERS = [
  { owner: "vitaliy", label: "Виталий" },
  { owner: "lina", label: "Лина" },
];

function TochkaAccountSelect({ account, owner, ownerLabel, bankItems, onLink }) {
  const current = (account.links || []).find((link) => link.owner === owner);
  return (
    <select
      className="login-input"
      aria-label={`Счёт в Точке ${ownerLabel} ${account.title}`}
      value={current?.tochka_account_id || ""}
      onChange={(event) => onLink(account, owner, event.target.value || null)}
    >
      <option value="">Не привязан</option>
      {(bankItems || []).map((item) => (
        <option
          key={item.account_id}
          value={item.account_id}
          label={
            current?.tochka_account_id === item.account_id
              ? bankClosedLabel(ownerLabel, item.tail)
              : undefined
          }
          disabled={
            item.linked_finance_account_id != null
            && item.linked_finance_account_id !== account.id
          }
        >
          {bankOptionLabel(item)}
        </option>
      ))}
    </select>
  );
}

function TochkaAccountSelects({ account, bankItems, onLink }) {
  if (!expectsBankSlots(account)) return null;
  return TOCHKA_OWNERS.map((slot) => (
    <div className="fin-fund-link" key={slot.owner}>
      <span className="fin-slot-label">{slot.label}</span>
      <TochkaAccountSelect
        account={account}
        owner={slot.owner}
        ownerLabel={slot.label}
        bankItems={bankItems}
        onLink={onLink}
      />
    </div>
  ));
}

function AccountBalanceField({ account, drafts, onDraft, onSave }) {
  if ((account.links || []).length > 0) {
    return (
      <span className="fin-balance-bank">
        <span className="fin-balance-amount">{formatBankBalance(account.balance)}</span>
        <span className="fin-balance-stamp">{`из банка ${formatBankClock(account.balance_synced_at)}`}</span>
      </span>
    );
  }
  return (
    <input
      className="login-input"
      aria-label={`Остаток ${account.title}`}
      value={draftValue(drafts, account.id, account.balance == null ? "" : String(account.balance))}
      onChange={(event) => onDraft(account.id, event.target.value)}
      onBlur={() => onSave(account)}
    />
  );
}

function BankPanel({ bank, onSynced }) {
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState(null);
  const [syncError, setSyncError] = useState("");
  const timerRef = useRef(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
      if (timerRef.current != null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  const pollStatus = useCallback(async (elapsed = 0) => {
    try {
      const status = await fetchBankSyncStatus();
      if (cancelledRef.current) return;
      if (status?.finished_at) {
        setSyncing(false);
        setResult(status);
        await onSynced();
        return;
      }
    } catch (err) {
      if (cancelledRef.current) return;
      setSyncing(false);
      setSyncError(getApiErrorMessage(err, "Не удалось получить статус"));
      return;
    }
    if (elapsed >= 5 * 60 * 1000) {
      setSyncing(false);
      setSyncError("Синхронизация не завершилась, обновите страницу");
      return;
    }
    timerRef.current = setTimeout(() => {
      pollStatus(elapsed + 2000);
    }, 2000);
  }, [onSynced]);

  const handleSync = async () => {
    setResult(null);
    setSyncError("");
    setSyncing(true);
    try {
      await startBankSync();
      if (cancelledRef.current) return;
      await pollStatus();
    } catch (err) {
      if (cancelledRef.current) return;
      setSyncing(false);
      setSyncError(getApiErrorMessage(err, "Не удалось запустить синхронизацию"));
    }
  };

  return (
    <section className="fin-section fin-panel" aria-label="Банк">
      <h3>Банк</h3>
      <p>Выписки: {formatBankStamp(bank?.synced_at)}</p>
      <p>Остатки: {formatBankClock(bank?.balances_at)}</p>
      {bank?.enabled === false ? (
        <div className="fin-warning">
          Синхронизация выключена: не задан TOCHKA_CUSTOMER_CODE
        </div>
      ) : (
        <button
          type="button"
          className="btn"
          disabled={syncing}
          onClick={handleSync}
        >
          Обновить из банка
        </button>
      )}
      {result?.error ? (
        <div className="login-error" role="status">{result.error}</div>
      ) : result ? (
        <div role="status">Добавлено {result.rows_added} операций</div>
      ) : null}
      {syncError ? (
        <div className="login-error" role="status">{syncError}</div>
      ) : null}
    </section>
  );
}

function compareAccounts(a, b) {
  return (Number(a.sort_order) - Number(b.sort_order)) || (Number(a.id) - Number(b.id));
}

function visibleOfKind(accounts, kind) {
  return [...(accounts || [])]
    .filter((item) => !item.archived && item.kind === kind)
    .sort(compareAccounts);
}

function draftValue(drafts, id, fallback) {
  return Object.prototype.hasOwnProperty.call(drafts, id) ? drafts[id] : fallback;
}

function currentPct(fund, drafts) {
  const raw = draftValue(drafts, fund.id, fund.pct == null ? "" : String(fund.pct));
  if (!String(raw ?? "").trim()) return null;
  const parsed = parseAmount(raw);
  return parsed.ok ? parsed.value : fund.pct;
}

function sumFundPct(funds, drafts, editedId, editedValue) {
  return funds.reduce((sum, fund) => {
    const pct = fund.id === editedId ? editedValue : currentPct(fund, drafts);
    return sum + (pct == null ? 0 : Number(pct));
  }, 0);
}

export default function FinanceAccounts() {
  const { month, setMonth, overview, loading, error, reload } = useFinanceOverview();
  const [balanceDrafts, setBalanceDrafts] = useState({});
  const [pctDrafts, setPctDrafts] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saveNotice, setSaveNotice] = useState("");
  const [actionError, setActionError] = useState("");
  const [bankItems, setBankItems] = useState([]);

  const accounts = overview?.accounts;
  const funds = useMemo(() => visibleOfKind(accounts, "fund"), [accounts]);
  const cashAccounts = useMemo(() => visibleOfKind(accounts, "account"), [accounts]);

  const resetNotices = () => {
    setSaveNotice("");
    setActionError("");
  };

  const loadBankAccounts = useCallback(async () => {
    try {
      const data = await fetchBankAccounts();
      setBankItems(data?.items || []);
    } catch (err) {
      setBankItems([]);
      setSaveNotice("");
      setActionError(getApiErrorMessage(err, "Не удалось загрузить счета банка"));
    }
  }, []);

  useEffect(() => {
    loadBankAccounts();
  }, [loadBankAccounts]);

  const afterSave = async () => {
    setSaveNotice("Сохранено");
    await reload();
    await loadBankAccounts();
  };

  const afterBankSync = useCallback(async () => {
    await reload();
    await loadBankAccounts();
  }, [reload, loadBankAccounts]);

  const failSave = (err) => {
    setSaveNotice("");
    setActionError(getApiErrorMessage(err, "Не удалось сохранить"));
  };

  const linkTochka = async (account, owner, tochkaAccountId) => {
    resetNotices();
    try {
      await putFinanceAccountLink(account.id, { tochka_account_id: tochkaAccountId, owner });
      await afterSave();
    } catch (err) {
      failSave(err);
    }
  };

  const saveBalance = async (account) => {
    resetNotices();
    const raw = draftValue(
      balanceDrafts,
      account.id,
      account.balance == null ? "" : String(account.balance)
    );
    const parsed = parseAmount(raw);
    if (!parsed.ok) return;
    if (Number(account.balance) === parsed.value) return;
    try {
      await patchFinanceAccount(account.id, { balance: parsed.value });
      await afterSave();
    } catch (err) {
      failSave(err);
    }
  };

  const savePct = async (fund) => {
    resetNotices();
    const raw = draftValue(pctDrafts, fund.id, fund.pct == null ? "" : String(fund.pct));
    let nextPct;
    if (!String(raw).trim()) {
      nextPct = null;
    } else {
      const parsed = parseAmount(raw);
      if (!parsed.ok) return;
      nextPct = parsed.value;
    }
    const current = fund.pct == null ? null : Number(fund.pct);
    if (current === nextPct) return;
    if (sumFundPct(funds, pctDrafts, fund.id, nextPct) > 100) {
      setActionError("Сумма процентов больше 100");
      return;
    }
    try {
      await patchFinanceAccount(fund.id, { pct: nextPct });
      await afterSave();
    } catch (err) {
      failSave(err);
    }
  };

  const archive = async (account) => {
    if (!window.confirm(`Архивировать «${account.title}»?`)) return;
    resetNotices();
    try {
      await patchFinanceAccount(account.id, { archived: true });
      await afterSave();
    } catch (err) {
      failSave(err);
    }
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    const title = form.title.trim();
    if (!title) return;
    resetNotices();
    const payload = {
      title,
      kind: form.kind,
      owner: form.owner,
      balance: 0,
    };
    if (form.kind === "fund") {
      const parsedPct = parseAmount(form.pct);
      payload.pct = parsedPct.ok ? parsedPct.value : null;
      payload.role = form.role || null;
    }
    try {
      await createFinanceAccount(payload);
      setShowForm(false);
      setForm(EMPTY_FORM);
      await afterSave();
    } catch (err) {
      failSave(err);
    }
  };

  return (
    <div className="page-container fin-page">
      <FinanceTabs month={month} onMonthChange={setMonth} title="Фонды и счета" />
      {error && <ErrorRetry message={error} onRetry={() => reload()} />}
      {saveNotice && (
        <div className="fin-save-ok" role="status">{saveNotice}</div>
      )}
      {actionError && (
        <div className="login-error" role="status" aria-live="polite">{actionError}</div>
      )}
      {loading && !overview ? (
        <Loading text="Загрузка кассы…" />
      ) : overview ? (
        <>
          <BankPanel bank={overview.bank} onSynced={afterBankSync} />
          <section className="fin-section" aria-label="Фонды">
            <h3>Фонды</h3>
            <div className="fin-kpis">
              {funds.map((fund) => (
                <div key={fund.id} className="stat-card fin-fund-card">
                  <span className="fin-kpi-caption">{fund.title}</span>
                  <strong>
                    <AccountBalanceField
                      account={fund}
                      drafts={balanceDrafts}
                      onDraft={(id, value) => {
                        setBalanceDrafts((prev) => ({ ...prev, [id]: value }));
                      }}
                      onSave={saveBalance}
                    />
                  </strong>
                  <small>
                    <TochkaAccountSelects
                      account={fund}
                      bankItems={bankItems}
                      onLink={linkTochka}
                    />
                    <p className="fin-fund-pct">
                      {fund.pct != null ? `${formatPct(fund.pct)} % от прихода` : "Нет автопополнения"}
                    </p>
                    {fund.role ? (
                      <p className="fin-account-owner">{roleLabel(fund.role)}</p>
                    ) : null}
                    <input
                      className="login-input"
                      aria-label={`Процент ${fund.title}`}
                      value={draftValue(
                        pctDrafts,
                        fund.id,
                        fund.pct == null ? "" : String(fund.pct)
                      )}
                      onChange={(e) => {
                        setPctDrafts((prev) => ({ ...prev, [fund.id]: e.target.value }));
                      }}
                      onBlur={() => savePct(fund)}
                    />
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => archive(fund)}
                    >
                      Архивировать
                    </button>
                  </small>
                </div>
              ))}
            </div>
            <p className="fin-free-pct">
              Свободно: {formatPct(overview.forecast?.free_pct)} %
            </p>
          </section>

          <section className="fin-section fin-panel" aria-label="Счета">
            <h3>Счета</h3>
            <div className="fin-table-wrap">
              <table className="fin-accounts">
                <thead>
                  <tr>
                    <th>Счёт</th>
                    <th>Владелец</th>
                    <th>Остаток</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {cashAccounts.map((account) => (
                    <tr key={account.id}>
                      <td>
                        {account.title}
                        <TochkaAccountSelects
                          account={account}
                          bankItems={bankItems}
                          onLink={linkTochka}
                        />
                      </td>
                      <td>{ownerLabel(account.owner)}</td>
                      <td>
                        <AccountBalanceField
                          account={account}
                          drafts={balanceDrafts}
                          onDraft={(id, value) => {
                            setBalanceDrafts((prev) => ({ ...prev, [id]: value }));
                          }}
                          onSave={saveBalance}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => archive(account)}
                        >
                          Архивировать
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowForm((open) => !open)}
          >
            Добавить
          </button>
          {showForm && (
            <form className="fin-inline-form fin-form-card" onSubmit={handleCreate}>
              <label className="login-label">
                Тип
                <select
                  className="login-input"
                  value={form.kind}
                  onChange={(e) => setForm({ ...form, kind: e.target.value })}
                >
                  <option value="account">Счёт</option>
                  <option value="fund">Фонд</option>
                </select>
              </label>
              <label className="login-label">
                Название
                <input
                  className="login-input"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  required
                />
              </label>
              <label className="login-label">
                Владелец
                <select
                  className="login-input"
                  value={form.owner}
                  onChange={(e) => setForm({ ...form, owner: e.target.value })}
                >
                  {OWNERS.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </select>
              </label>
              {form.kind === "fund" && (
                <>
                  <label className="login-label">
                    %
                    <input
                      className="login-input"
                      value={form.pct}
                      onChange={(e) => setForm({ ...form, pct: e.target.value })}
                    />
                  </label>
                  <label className="login-label">
                    Роль
                    <select
                      className="login-input"
                      value={form.role}
                      onChange={(e) => setForm({ ...form, role: e.target.value })}
                    >
                      <option value="">без роли</option>
                      {ROLES.map((item) => (
                        <option key={item.value} value={item.value}>{item.label}</option>
                      ))}
                    </select>
                  </label>
                </>
              )}
              <button type="submit" className="btn">Создать</button>
            </form>
          )}
        </>
      ) : null}
    </div>
  );
}
